import { useState } from 'react';
import { PrototypeContactNote } from '../components/Prototype';
import { BUSINESS, telHref } from '../config/business';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import styles from './ContactPage.module.css';

const hasDirectContact = Boolean(
  BUSINESS.phone || BUSINESS.email || BUSINESS.serviceArea,
);

type Status = 'idle' | 'submitting' | 'success' | 'error';

function encodeFormData(data: Record<string, string>): string {
  return Object.entries(data)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');
}

export function ContactPage() {
  useDocumentTitle('Contact');
  const [status, setStatus] = useState<Status>('idle');

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus('submitting');

    const form = event.currentTarget;
    const formData = new FormData(form);
    const payload: Record<string, string> = { 'form-name': 'contact' };
    formData.forEach((value, key) => {
      payload[key] = String(value);
    });

    try {
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encodeFormData(payload),
      });
      if (!response.ok) throw new Error(`Request failed with status ${response.status}`);
      setStatus('success');
      form.reset();
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="container">
      <header className={styles.pageHeader}>
        <h1 className={styles.title}>Let&rsquo;s talk about your project</h1>
        <p className={styles.lede}>
          Tell us a bit about what you&rsquo;re looking to build, and we&rsquo;ll follow
          up to set up a time to talk.
        </p>
        {BUSINESS.responseTime && (
          <p className={styles.responseTime}>{BUSINESS.responseTime}</p>
        )}
        {hasDirectContact && (
          <ul className={styles.directContact}>
            {BUSINESS.phone && (
              <li>
                <span className={styles.directLabel}>Call</span>
                <a href={telHref(BUSINESS.phone)}>{BUSINESS.phone}</a>
              </li>
            )}
            {BUSINESS.email && (
              <li>
                <span className={styles.directLabel}>Email</span>
                <a href={`mailto:${BUSINESS.email}`}>{BUSINESS.email}</a>
              </li>
            )}
            {BUSINESS.serviceArea && (
              <li>
                <span className={styles.directLabel}>Area</span>
                <span>{BUSINESS.serviceArea}</span>
              </li>
            )}
          </ul>
        )}
        <PrototypeContactNote />
      </header>

      {status === 'success' ? (
        <div className={styles.success} role="status">
          <p className={styles.successTitle}>Thanks — message sent.</p>
          <p className={styles.successBody}>We&rsquo;ll be in touch soon.</p>
        </div>
      ) : (
        <form
          name="contact"
          onSubmit={handleSubmit}
          className={styles.form}
          data-netlify="true"
          {...{ 'netlify-honeypot': 'bot-field' }}
        >
          <input type="hidden" name="form-name" value="contact" />
          {/* Spam trap: invisible, unfocusable, and hidden from screen readers. */}
          <p className={styles.honeypot} aria-hidden="true">
            <label>
              Don&rsquo;t fill this out if you&rsquo;re human:{' '}
              <input name="bot-field" tabIndex={-1} autoComplete="off" />
            </label>
          </p>

          <label className={styles.field}>
            <span>Name</span>
            <input type="text" name="name" required autoComplete="name" />
          </label>

          <label className={styles.field}>
            <span>Email</span>
            <input type="email" name="email" required autoComplete="email" />
          </label>

          <label className={styles.field}>
            <span>Phone</span>
            <input type="tel" name="phone" autoComplete="tel" />
          </label>

          <label className={styles.field}>
            <span>Message</span>
            <textarea name="message" rows={5} required />
          </label>

          <button
            type="submit"
            className={`btn btn-primary ${styles.submit}`}
            disabled={status === 'submitting'}
          >
            {status === 'submitting' ? 'Sending…' : 'Send message'}
          </button>

          {status === 'error' && (
            <p className={styles.errorText} role="alert">
              Something went wrong sending that — please try again.
            </p>
          )}
        </form>
      )}
    </div>
  );
}
